//#region Imports Packages and Libs

import { Component, Prop, Vue, Watch } from "vue-facing-decorator";
import { createLogger } from "../../helpers/logger";
import { __vueEvent } from "../../constant";
import { EVENTS } from "../../enums";
import { isMobile } from "../../helpers/is_mobile";
import { FeedbackService } from "../../service/message/feedback_service";
import { FeedbackKeys, SaveFeedbackParams } from "../../types";
import { TimeoutId } from "../../types/components/transcript";
import { StringUtils } from "../../helpers/string_utils";
import { Util } from "../../util";
import { getDefaultFeedbackFormLabel, resolveFeedbackFormLabel } from "../../helpers/i18n/label_resolver";

//#endregion

//#region Define the component in class-style

@Component({})
export default class FeedbackModal extends Vue {
  //#region Class properties will be component data

  /** @property messageData - The data for the message being updated */
  @Prop({ type: Object, default: () => ({}) }) messageData?: any;

  // Form data
  feedbackText = "";
  isSubmitting = false;
  successMessage = "";
  errorMessage = "";

  // Logger instance for logging errors and messages
  private logger = createLogger({ context: "feedback_modal.ts" });

  // Track timeout for auto-hiding general error messages
  errorTimeout: TimeoutId | null = null;
  successTimeout: TimeoutId | null = null;

  //#endregion

  //#region Class constants

  /**
   * Time in milliseconds to display error messages before auto-hiding
   */
  private readonly ERROR_MESSAGE_TIMEOUT_MS: number = 5000;
  private readonly SUCCESS_MESSAGE_TIMEOUT_MS: number = 3000;

  //#endregion

  //#region Computed Properties

  /**
   * Get the labels for the thumbs down feedback quick reply buttons.
   */
  get feedbackDownButtonLabels(): string[] {
    try {
      /**
       * JIRA - SD-10045
       * To implement the multi-language feature the quick replies are hardcoded at the moment
       */
      const pageConfig = this.$store.state.pageConfig;
      return resolveFeedbackFormLabel("quick_replies", pageConfig) as string[];
    } catch (ex) {
      return getDefaultFeedbackFormLabel("quick_replies") as string[];
    }
  }

  /**
   * Gets whether the form can be submitted.
   * @returns {boolean} True if the form can be submitted, false otherwise.
   */
  get canSubmit(): boolean {
    try {
      return (
        this.feedbackText?.trim().length > 0 &&
        !this.isSubmitting &&
        StringUtils.isNullOrWhitespace(this.successMessage)
      );
    } catch {
      return false;
    }
  }

  /**
   * Build aria-describedby ids only when targets exist in DOM.
   */
  get feedbackAriaDescribedBy(): string {
    try {
      const ids: string[] = ["feedbackHint"];
      if (!StringUtils.isNullOrWhitespace(this.errorMessage)) ids.push("feedbackError");
      if (!StringUtils.isNullOrWhitespace(this.successMessage)) ids.push("feedbackSuccess");
      return ids.join(" ");
    } catch {
      return "feedbackHint";
    }
  }

  //#endregion

  //#region Modal Lifecycle Hooks

  /**
   * Vue lifecycle hook called when the component is mounted
   * This is where we set up event listeners for showing/hiding the modal
   */
  mounted(): void {
    this.focusFirstElement();
		this.refreshDomLabels(); // initialize labels on first render
  }

  /**
   * Vue lifecycle hook called just before the component is destroyed
   * This is where we clean up resources to prevent memory leaks
   */
  beforeDestroy(): void {
    this.cleanupResources();
  }

  //#region Modal related logic here

  /**
   * This method is called when the user clicks the close modal button or clicks outside modal.
   */
  closeModal(): void {
    try {
      __vueEvent.emit(EVENTS.HideFeedbackModal);
    } catch (ex) {
      this.logError("closeModal", ex);
    }
  }

  /**
   * Handles quick reply button clicks
   */
  handleQuickReply(text: string): void {
    this.feedbackText = text;
    this.errorMessage = "";
  }

  /**
   * Focuses the first input element (feedbackText field) in the modal after the next DOM update.
   */
  focusFirstElement(): void {
    // Focus the first input element in the modal when it opens
    this.$nextTick(() => {
      const firstInput = this.$refs.feedbackText as HTMLInputElement;

      // No need to focus if the chat modal is opened in mobile devices,
      // to prevent virtual keyboard issues on mobile & iOS devices
      if (firstInput && !isMobile.any()) {
        firstInput.focus({ preventScroll: true });
      }
    });
  }

  /**
   * Sets general error message and schedules auto-hide after a specified timeout
   * Creates a timeout that will be automatically cleared when the component is destroyed
   *
   * @param {string} message - Error message to display
   */
  setErrorWithTimeout(message: string): void {
    try {
      // Clear any existing timeout first to prevent memory leaks
      this.clearErrorTimeout();

      // Early return for empty or invalid messages
      if (!message || typeof message !== "string" || message.trim() === "") {
        return;
      }

      this.errorMessage = message;
      this.successMessage = "";

      this.errorTimeout = setTimeout(() => {
        this.errorMessage = "";
        this.errorTimeout = null;
      }, this.ERROR_MESSAGE_TIMEOUT_MS);
    } catch (ex) {
      this.logError("setErrorWithTimeout", ex);
    }
  }
  
  /**
   * SD-10045: Updates the button label based on submission state.
   * When `isSubmitting` is true → show "Sending..."
   * When `isSubmitting` is false → show normal "Send" label
   */
  get buttonText(): string {
    return this.isSubmitting ? this.getFeedbackLabel("send_button_sending") : this.getFeedbackLabel("send_button_label")
  }
  
  /**
   * Sets a success message and schedules auto-hide after a specified timeout
   * Creates a timeout that will be automatically cleared when the component is destroyed
   *
   * @param {string} message - Success message to display
   */
  setSuccessWithTimeout(message: string): void {
    try {
      // Clear any existing timeout first to prevent memory leaks
      this.clearSuccessTimeout();

      // Early return for empty or invalid messages
      if (!message || typeof message !== "string" || message.trim() === "") {
        return;
      }

      this.successMessage = message;
      this.errorMessage = "";
      this.successTimeout = setTimeout(() => {
        // Close the modal if it's still open
        this.closeModal();
        this.successMessage = "";
        this.successTimeout = null;
      }, this.SUCCESS_MESSAGE_TIMEOUT_MS);
    } catch (ex) {
      this.logError("setSuccessWithTimeout", ex);
    }
  }

  /**
   * Clears the error timeout if it exists
   */
  clearErrorTimeout(): void {
    try {
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
        this.errorTimeout = null;
      }
    } catch (ex) {
      this.logError("clearErrorTimeout", ex);
    }
  }

  /**
   * Clears the success timeout if it exists
   */
  clearSuccessTimeout(): void {
    try {
      if (this.successTimeout) {
        clearTimeout(this.successTimeout);
        this.successTimeout = null;
      }
    } catch (ex) {
      this.logError("clearSuccessTimeout", ex);
    }
  }

  //#endregion

  //#region Form Submission Logic

  /**
   * Normalizes text input by removing extra whitespace and standardizing line breaks.
   *
   * @param text - The text string to normalize
   * @returns A normalized string with consistent spacing and line breaks, or an empty string if input is invalid
   */
  private normalizeInput(text: string): string {
    try {
      if (!text || typeof text !== "string" || text.length === 0) return "";

      // normalize CRLF -> LF
      let s = text.replace(/\r\n?/g, "\n");

      // collapse 2+ newlines → single newline
      s = s.replace(/\n{2,}/g, "\n");

      // collapse horizontal whitespace (spaces, tabs, etc.) but preserve newlines
      s = s.replace(/[^\S\n]{2,}/g, " ");

      // trim leading/trailing whitespace
      return Util.trimString(s);
    } catch (ex) {
      //this.logError("normalizeInput", ex);
      return text;
    }
  }

  /**
   * Return a single cleaned/sanitized feedback string.
   * - Normalizes line breaks & whitespace
   * - Removes HTML/script tags
   * - Trims final value
   */
  private getCleanedFeedback(): string {
    try {
      const raw = this.feedbackText ?? "";
      // normalize (preserves single newlines, collapses extra spaces)
      let cleaned = this.normalizeInput(raw);
      // sanitize (remove tags / unsafe content)
      cleaned = Util.sanitizeInput(cleaned);
      return Util.trimString(cleaned);
    } catch (ex) {
      return this.feedbackText ?? "";
    }
  }

  /**
   * Validates the feedback form input.
   *
   * @returns {boolean} True if the form input is valid, false otherwise
   */
  private validateForm(): boolean {
    try {
      this.clearErrorTimeout();
      const cleaned = this.getCleanedFeedback();

      // Checks if the cleaned input has content
      if (cleaned.length === 0) {
        // this.setErrorWithTimeout("Please enter your feedback");
        this.setErrorWithTimeout(this.getFeedbackLabel("feedback_fail"));
        return false;
      }

      return true;
    } catch (ex) {
      this.logError("validateForm", ex);
      return false;
    }
  }

  /**
   * Builds the API payload object for saving feedback.
   *
   * @returns {SaveFeedbackParams} An object containing parameters required for saving feedback
   */
  private buildApiPayload(): SaveFeedbackParams {
    const cleanedFeedbackText = this.getCleanedFeedback();
    return {
      ExtBotId: this.$store.state.extBotId || 0,
      ExtMsgId: this.messageData?.extMsgId || "",
      Key: FeedbackKeys.Feedback,
      Value: cleanedFeedbackText,
      ConvId: this.$store.state.convId || "",
      CompanyId: Number(this.$store.state.companyId) || 0,
    };
  }

  /**
   * Send the feedback payload to the feedback service.
   *
   * @param apiPayload - payload shaped for the backend API
   * @returns Promise resolving with the service response
   */
  private async sendFeedbackToService(apiPayload: SaveFeedbackParams) {
    const svc = new FeedbackService();
    return svc.saveMessageFeedback(apiPayload);
  }

  /**
   * Submits feedback to server
   */
  async submitFeedback(): Promise<void> {
    try {
      // Validate the form before submission
      if (!this.validateForm()) return;
      this.isSubmitting = true;
      this.errorMessage = "";

      // Create feedback service instance
      const apiPayload = this.buildApiPayload();
      const response = await this.sendFeedbackToService(apiPayload);

      // Handle successful response, when "status" is true
      if (!!response?.status) {
        this.setSuccessWithTimeout(this.getFeedbackLabel("feedback_success"));
      } else {
        // this.setErrorWithTimeout("Failed to send your feedback. Please try again.");
        this.setErrorWithTimeout(this.getFeedbackLabel("feedback_fail"));
        this.logError(
          "submitFeedback",
          response?.statusMessage || "Failed to send your feedback. Please try again.",
          "Feedback submission"
        );
      }
    } catch (ex) {
      this.logError("submitFeedback", ex, "Feedback submission error");
      // this.setErrorWithTimeout("An error occurred while sending your feedback. Please try again.");
        this.setErrorWithTimeout(this.getFeedbackLabel("feedback_fail"));
    } finally {
      this.isSubmitting = false;
    }
  }

  //#endregion
  
  //#region Refresh DOM Labels - SD-10045: Implementing multi language with Feedback form
  
  /**
   * Retrieve a localized label specific to the feedback form modal by key, using default labels as fallback.
   */
  private getFeedbackLabel(key: string): string {
    try {
      const pageConfig = this.$store.state.pageConfig;
      return resolveFeedbackFormLabel(key, pageConfig) as string;
    } catch (ex) {
      return getDefaultFeedbackFormLabel(key) as string;
    }
  }
  
  /**
   * Safely assigns the provided text to the element's textContent if the element appears to be an HTMLElement.
   * @param element - The target element (of unknown type). 
   * @param text - The text to set on the element's textContent.
   */
  private setElementText(element: unknown, text: string): void {
    try {
      if (element && typeof (element as HTMLElement).textContent === 'string') {
        (element as HTMLElement).textContent = text;
      }
    } catch {}
  }
  
  /**
   * Sets multiple attributes on a target element if it implements setAttribute. 
   * @param element - The target to receive attributes. 
   * @param attributes - A record mapping attribute names to string values. 
   */
  private setElementAttributes(element: unknown, attributes: Record<string, string>): void {
    try {
      if (!element || !(element as HTMLElement).setAttribute) return;
      for (const [attrName, attrValue] of Object.entries(attributes)) {
        (element as HTMLElement).setAttribute(attrName, attrValue);
      }
    } catch {}
  }

  /**
   * This method refreshes text content, placeholders, titles, and aria attributes
   * for various UI elements in the feedback form modal based on configured labels.
   */
  private refreshDomLabels(): void {
    try {
      this.$nextTick(() => {
        const formHeadingLine = this.getFeedbackLabel("form_heading_line")
        const feedbackTextPlaceholder = this.getFeedbackLabel("feedback_text_placeholder")
        const closeText = this.getFeedbackLabel("close_modal")
        
        // Close button (title + aria-label)
        this.setElementAttributes(this.$refs.closeButton, { title: closeText, "aria-label": closeText });

        // Heading paragraph (first <p> near the top of the form)
        this.setElementText(this.$refs.formHeadingLine, formHeadingLine);
        this.setElementText(this.$refs.feedbackHint, feedbackTextPlaceholder);
        
        // Inputs: placeholder
        this.setElementAttributes(this.$refs.feedbackText, { placeholder: feedbackTextPlaceholder });
      });
    } catch (error) {
      this.logError("refreshDomLabels", error);
    }
  }
  
  //#endregion

  //#region Utility Methods

  /**
   * Logs errors in a standardized format across the component
   *
   * @param {string} methodName - Name of the method where error occurred
   * @param {unknown} error - The error that occurred
   * @param {string} [context] - Optional additional context about the error
   */
  logError(methodName: string, error: unknown, context?: string): void {
    const contextInfo = context ? ` - ${context}` : "";
    this.logger.error(`Error in ${methodName}${contextInfo}:`, error);
  }

  /**
   * Cleans up all resources used by the component
   * Ensures all timeouts are cleared to prevent memory leaks
   */
  cleanupResources(): void {
    try {
      // Clear all timeouts
      this.clearErrorTimeout();
      this.clearSuccessTimeout();
    } catch (ex) {
      this.logError("cleanupResources", ex);
    }
  }

  //#endregion
}

//#endregion
